import { useCallback, useMemo, useRef, useState } from 'react';
import { addEdge, applyEdgeChanges, applyNodeChanges, ReactFlowProvider } from 'reactflow';
import SmtpNode from './nodes/OtherNodes/SmtpNode';
import { PcNode } from './nodes/OtherNodes/PcNode';
import TelemetryNode from './nodes/OtherNodes/TelemetryNode';
import { CommonZone } from './nodes/ZonesNodes/CommonZone';
import { VRRPNode } from './nodes/OtherNodes/VRRPNode';
import FlowSidebar from './FlowSidebar';
import { Alert } from '@mui/lab';
import FloatingEdge from './edges/FloatingEdge';
import FlowInstance from './FlowInstance';
import PropTypes from 'prop-types';
import 'reactflow/dist/style.css';
import './css/Node.css';
import './css/Flow.css';
import { NgfwNode } from './nodes/UGnodes/NgfwNode';
import { McNode } from './nodes/UGnodes/McNode';
import { LoganNode } from './nodes/UGnodes/LoganNode';
import classNames from 'classnames';
import { generateUniqueStringKey } from '@shared/utils/utilsOld';
import PPPoENode from './nodes/OtherNodes/PPPoENode';
import { Box } from '@mui/material';
import WindowsNode from './nodes/OtherNodes/WindowsNode';
import { AdNode } from './nodes/OtherNodes/AdNode';
import IcapNode from './nodes/OtherNodes/IcapNode';
import { WafNode } from './nodes/UGnodes/WafNode';
import { SiemNode } from './nodes/UGnodes/SiemNode';
import RadiusNode from './nodes/OtherNodes/RadiusNode';
import TacacsNode from './nodes/OtherNodes/TacacsNode';
import { AstraLinuxNode } from './nodes/OtherNodes/AstraLinuxNode';
import { KaliLinuxNode } from './nodes/OtherNodes/KaliLinuxNode';
import { DcfwNode } from './nodes/UGnodes/DcfwNode';
import WebServerNode from './nodes/OtherNodes/WebServerNode';
import { WwwNode } from './nodes/AcademyNodes/WwwNode';
import { WwwNode1 } from './nodes/AcademyNodes/WwwNode1';
import { WwwNode2 } from './nodes/AcademyNodes/WwwNode2';
import { CiscoNode } from './nodes/OtherNodes/CiscoNode';
import { TrexNode } from './nodes/OtherNodes/TrexNode';
import { FtpNode } from './nodes/OtherNodes/FtpNode';
import { RedosNode } from './nodes/OtherNodes/RedosNode';
import { SwgNode } from './nodes/UGnodes/SwgNode';
import { CollectorNode } from './nodes/UGnodes/CollectorNode';
import VulnerableAppNode from './nodes/OtherNodes/VulnerableAppNode';
import { IspNode1 } from './nodes/AcademyNodes/IspNode1';
import { IspNode2 } from './nodes/AcademyNodes/IspNode2';
import { CliALXNode } from './nodes/AcademyNodes/CliALXNode';
import { MgtANode } from './nodes/AcademyNodes/MgtANode';
import { InternetNode } from './nodes/AcademyNodes/InternetNode';
import { TestAgentNode } from './nodes/OtherNodes/TestAgentNode';
import { PtNgfwNode } from './nodes/OtherNodes/PtNgfwNode';
import { PtMgmtNode } from './nodes/OtherNodes/PtMgmtNode';
import { NODE_DEFAULT_PARAMS, NODE_DEFAULT_PORTS, ZONES_LIST } from '@pages/schemes/constants';
import { makeCorrectLoganBasedNodeRoles, removeUselessEdges } from '@pages/schemes/utils';
import { IspIntroNode } from './nodes/AcademyNodes/IspIntroNode';
import { MgtAIntroNode } from './nodes/AcademyNodes/MgtAIntroNode';
import { WecNode } from './nodes/OtherNodes/WecNode';
import { MikrotikNode } from './nodes/OtherNodes/MikrotikNode';
import EncryptorNode from './nodes/OtherNodes/EncryptorNode';
import { TechTourADNode } from './nodes/OtherNodes/TechTourADNode';

const proOptions = { hideAttribution: true };

const defaultEdgeOptions = {
    style: { strokeWidth: 1, stroke: '#9ca8b3' },
    type: 'floating',
    interactionWidth: 15,
    markerEnd: { type: 'arrowclosed' },
};

const snapGrid = [10, 10];
const defaultViewport = { x: 500, y: 250, zoom: 1 };

const TYPE_NAME_MAP = {
    ptmgmt: 'tpmgmt',
    ptngfw: 'tpngfw',
};

function Flow({
    readonly = false,
    configureOnly = false,
    nodes,
    edges,
    setNodes = () => {},
    setEdges = () => {},
    errorMessage,
    setErrorMessage,
    containerProps,
}) {
    const reactFlowWrapper = useRef(null);
    const nodeTypes = useMemo(
        () => ({
            ngfw: NgfwNode,
            collector: CollectorNode,
            dcfw: DcfwNode,
            mc: McNode,
            logan: LoganNode,
            waf: WafNode,
            siem: SiemNode,
            swg: SwgNode,
            ubuntu: PcNode,
            mikrotik: MikrotikNode,
            cli_a_lx: CliALXNode,
            mgt_a: MgtANode,
            mgt_a_intro: MgtAIntroNode,
            testagent: TestAgentNode,
            ptngfw: PtNgfwNode,
            ptmgmt: PtMgmtNode,
            cisco: CiscoNode,
            wec: WecNode,
            trex: TrexNode,
            astralinux: AstraLinuxNode,
            redos: RedosNode,
            kalilinux: KaliLinuxNode,
            lan: CommonZone,
            wan: CommonZone,
            mgmt: CommonZone,
            dmz: CommonZone,
            cluster: CommonZone,
            vrrp: VRRPNode,
            pppoeserver: PPPoENode,
            windows: WindowsNode,
            encryptor: EncryptorNode,
            ad: AdNode,
            tech_tour_ad: TechTourADNode,
            isp1: IspNode1,
            isp2: IspNode2,
            isp_intro: IspIntroNode,
            ftp: FtpNode,
            telemetryservice: TelemetryNode,
            smtp: SmtpNode,
            icap: IcapNode,
            radius: RadiusNode,
            tacacs: TacacsNode,
            webserver: WebServerNode,
            www: WwwNode,
            www1: WwwNode1,
            www2: WwwNode2,
            vulnerableapp: VulnerableAppNode,
            internet: InternetNode,
        }),
        [],
    );
    const edgeTypes = useMemo(() => ({ floating: FloatingEdge }), []);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [showMiniMap, setShowMiniMap] = useState(false);

    const onNodesChange = useCallback(
        (changes) =>
            setNodes((nds) => {
                removeUselessEdges(changes, nds, setEdges); // if port was deleted, edges including this port must be deleted

                if (changes[0].id && changes[0].type === 'remove') {
                    nds = makeCorrectLoganBasedNodeRoles(nds, changes[0].id);
                }

                return applyNodeChanges(changes, nds);
            }),
        [setNodes, setEdges],
    );

    const onEdgesChange = useCallback(
        (changes) =>
            setEdges((eds) => {
                // animate selected edges
                const selectChanges = changes.filter((change) => change.type === 'select');
                const newEds = structuredClone(eds);
                selectChanges.forEach((change) => {
                    const selectedEdgeIndex = newEds.findIndex((ed) => ed.id === change.id);
                    if (selectedEdgeIndex !== -1) {
                        newEds[selectedEdgeIndex] = { ...newEds[selectedEdgeIndex], animated: change.selected };
                    }
                });

                return applyEdgeChanges(changes, newEds);
            }),
        [setEdges],
    );

    const onConnect = useCallback(
        (connection) => {
            setEdges((eds) => addEdge(connection, eds));
        },
        [setEdges],
    );

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
            const type = event.dataTransfer.getData('application/Flow');

            // check if the dropped element is valid
            if (typeof type === 'undefined' || !type) {
                return;
            }

            const position = reactFlowInstance.project({
                x: event.clientX - reactFlowBounds.left,
                y: event.clientY - reactFlowBounds.top,
            });

            const existingNodes = reactFlowInstance.getNodes();
            const nodesOfThisTypeCount = existingNodes.filter((node) => node.type === type).length;
            const isZoneNode = ZONES_LIST.includes(type);
            const displayType = TYPE_NAME_MAP[type] || type;

            const newNode = {
                id: generateUniqueStringKey(type),
                type,
                position,
                data: {
                    ports: structuredClone(NODE_DEFAULT_PORTS[type]),
                    rotation: 0,
                    ...(isZoneNode ? undefined : { name: `${displayType.toUpperCase()} ${nodesOfThisTypeCount + 1}` }),
                    ...(isZoneNode ? { type: 'static', index: 1 } : undefined),
                    ...NODE_DEFAULT_PARAMS[type],
                },
                selected: false,
                dragging: false,
            };
            setNodes((nds) => nds.concat(newNode));
        },
        [reactFlowInstance, setNodes],
    );

    const isValidConnection = (connection) => {
        const targetNodeId = connection.target;
        const sourceNodeId = connection.source;
        const sourceHandle = connection.sourceHandle;
        const targetNode = nodes.find((node) => node.id === targetNodeId);
        const sourceNode = nodes.find((node) => node.id === sourceNodeId);
        const targetNodeType = targetNode?.type;
        const sourceNodeType = sourceNode?.type;

        switch (sourceNodeType) {
            case 'vrrp': {
                if (!ZONES_LIST.includes(targetNodeType)) {
                    setErrorMessage("You can't connect VRRP not to zone");
                    setTimeout(setErrorMessage, 3000, null);
                    return false;
                }
                break;
            }
            case 'mc':
            case 'ubuntu':
            case 'mikrotik':
            case 'ftp':
            case 'astralinux':
            case 'kalilinux':
            case 'redos':
            case 'cisco':
            case 'trex':
            case 'logan':
            case 'ngfw':
            case 'collector':
            case 'dcfw':
            case 'waf':
            case 'siem':
            case 'swg':
            case 'ad':
            case 'tech_tour_ad':
            case 'wec':
            case 'pppoeserver':
            case 'telemetryservice':
            case 'smtp':
            case 'radius':
            case 'tacacs':
            case 'windows':
            case 'encryptor': {
                if (sourceHandle === 'port0' && targetNodeType !== 'mgmt' && sourceNodeType === 'cisco') {
                    setErrorMessage('You can connect port0 only to MGMT zone!');
                    setTimeout(setErrorMessage, 3000, null);
                    return false;
                }

                if (edges.find((edge) => edge.source === connection.source && edge.sourceHandle === sourceHandle)) {
                    setErrorMessage('This port is already used');
                    setTimeout(setErrorMessage, 3000, null);
                    return false;
                }
                break;
            }
            default: {
                break;
            }
        }

        return true;
    };

    return (
        <Box className="reactflow" {...containerProps}>
            <ReactFlowProvider>
                <Box
                    data-testid="reactflow-wrapper"
                    className={classNames('reactflow__wrapper', {
                        'reactflow__wrapper-readonly': readonly,
                        'reactflow__wrapper-configure-only': configureOnly,
                    })}
                    ref={reactFlowWrapper}
                >
                    {errorMessage && (
                        <Alert
                            data-testid="reactflow-error-message"
                            className="reactflow__error-message"
                            severity="error"
                        >
                            <div className="reactflow__error-message-text">{errorMessage}</div>
                        </Alert>
                    )}
                    <FlowInstance
                        ref={reactFlowWrapper}
                        nodes={nodes}
                        edges={edges}
                        defaultViewport={defaultViewport}
                        defaultEdgeOptions={defaultEdgeOptions}
                        proOptions={proOptions}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onInit={setReactFlowInstance}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        fitView
                        disableKeyboardA11y={true}
                        isValidConnection={isValidConnection}
                        setShowMiniMap={setShowMiniMap}
                        showMiniMap={showMiniMap}
                        snapGrid={snapGrid}
                        readonly={readonly}
                        configureOnly={configureOnly}
                    />
                </Box>
                {!readonly && !configureOnly && <FlowSidebar />}
            </ReactFlowProvider>
        </Box>
    );
}

Flow.propTypes = {
    nodes: PropTypes.array.isRequired,
    edges: PropTypes.array.isRequired,
    errorMessage: PropTypes.string,
    setNodes: PropTypes.func,
    setEdges: PropTypes.func,
    setErrorMessage: PropTypes.func,
    handleClickOpen: PropTypes.func,
    handleImportClick: PropTypes.func,
};

export default Flow;
